import {Component, ViewChild, ViewEncapsulation, Input, Output, EventEmitter, Injectable } from '@angular/core';
import { ValueAccessorBase } from '../default-class/value-accessor';
import { NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import * as moment from 'moment';

// Month translations
const monthTranslations = {
  'January': {
    en: 'January',
    es: 'Enero',
    ar: 'يناير',
    fr: 'Janvier',
    pt: 'Janeiro',
    de: 'Januar',
    ja: '1月',
    it: 'Gennaio'
  },
  'February': {
    en: 'February',
    es: 'Febrero',
    ar: 'فبراير',
    fr: 'Février',
    pt: 'Fevereiro',
    de: 'Februar',
    ja: '2月',
    it: 'Febbraio'
  },
  'March': {
    en: 'March',
    es: 'Marzo',
    ar: 'مارس',
    fr: 'Mars',
    pt: 'Março',
    de: 'März',
    ja: '3月',
    it: 'Marzo'
  },
  'April': {
    en: 'April',
    es: 'Abril',
    ar: 'أبريل',
    fr: 'Avril',
    pt: 'Abril',
    de: 'April',
    ja: '4月',
    it: 'Aprile'
  },
  'May': {
    en: 'May',
    es: 'Mayo',
    ar: 'مايو',
    fr: 'Mai',
    pt: 'Maio',
    de: 'Mai',
    ja: '5月',
    it: 'Maggio'
  },
  'June': {
    en: 'June',
    es: 'Junio',
    ar: 'يونيو',
    fr: 'Juin',
    pt: 'Junho',
    de: 'Juni',
    ja: '6月',
    it: 'Giugno'
  },
  'July': {
    en: 'July',
    es: 'Julio',
    ar: 'يوليو',
    fr: 'Juillet',
    pt: 'Julho',
    de: 'Juli',
    ja: '7月',
    it: 'Luglio'
  },
  'August': {
    en: 'August',
    es: 'Agosto',
    ar: 'أغسطس',
    fr: 'Août',
    pt: 'Agosto',
    de: 'August',
    ja: '8月',
    it: 'Agosto'
  },
  'September': {
    en: 'September',
    es: 'Septiembre',
    ar: 'سبتمبر',
    fr: 'Septembre',
    pt: 'Setembro',
    de: 'September',
    ja: '9月',
    it: 'Settembre'
  },
  'October': {
    en: 'October',
    es: 'Octubre',
    ar: 'أكتوبر',
    fr: 'Octobre',
    pt: 'Outubro',
    de: 'Oktober',
    ja: '10月',
    it: 'Ottobre'
  },
  'November': {
    en: 'November',
    es: 'Noviembre',
    ar: 'نوفمبر',
    fr: 'Novembre',
    pt: 'Novembro',
    de: 'November',
    ja: '11月',
    it: 'Novembre'
  },
  'December': {
    en: 'December',
    es: 'Diciembre',
    ar: 'ديسمبر',
    fr: 'Décembre',
    pt: 'Dezembro',
    de: 'Dezember',
    ja: '12月',
    it: 'Dicembre'
  },
  // Short month names
  'Jan': {
    en: 'Jan',
    es: 'Ene',
    ar: 'ينا',
    fr: 'Jan',
    pt: 'Jan',
    de: 'Jan',
    ja: '1月',
    it: 'Gen'
  },
  'Feb': {
    en: 'Feb',
    es: 'Feb',
    ar: 'فبر',
    fr: 'Fév',
    pt: 'Fev',
    de: 'Feb',
    ja: '2月',
    it: 'Feb'
  },
  'Mar': {
    en: 'Mar',
    es: 'Mar',
    ar: 'مار',
    fr: 'Mar',
    pt: 'Mar',
    de: 'Mär',
    ja: '3月',
    it: 'Mar'
  },
  'Apr': {
    en: 'Apr',
    es: 'Abr',
    ar: 'أبر',
    fr: 'Avr',
    pt: 'Abr',
    de: 'Apr',
    ja: '4月',
    it: 'Apr'
  },
  'Jun': {
    en: 'Jun',
    es: 'Jun',
    ar: 'يون',
    fr: 'Juin',
    pt: 'Jun',
    de: 'Jun',
    ja: '6月',
    it: 'Giu'
  },
  'Jul': {
    en: 'Jul',
    es: 'Jul',
    ar: 'يول',
    fr: 'Juil',
    pt: 'Jul',
    de: 'Jul',
    ja: '7月',
    it: 'Lug'
  },
  'Aug': {
    en: 'Aug',
    es: 'Ago',
    ar: 'أغس',
    fr: 'Août',
    pt: 'Ago',
    de: 'Aug',
    ja: '8月',
    it: 'Ago'
  },
  'Sep': {
    en: 'Sep',
    es: 'Sep',
    ar: 'سبت',
    fr: 'Sep',
    pt: 'Set',
    de: 'Sep',
    ja: '9月',
    it: 'Set'
  },
  'Oct': {
    en: 'Oct',
    es: 'Oct',
    ar: 'أكت',
    fr: 'Oct',
    pt: 'Out',
    de: 'Okt',
    ja: '10月',
    it: 'Ott'
  },
  'Nov': {
    en: 'Nov',
    es: 'Nov',
    ar: 'نوف',
    fr: 'Nov',
    pt: 'Nov',
    de: 'Nov',
    ja: '11月',
    it: 'Nov'
  },
  'Dec': {
    en: 'Dec',
    es: 'Dic',
    ar: 'ديس',
    fr: 'Déc',
    pt: 'Dez',
    de: 'Dez',
    ja: '12月',
    it: 'Dic'
  }
};

@Injectable()
export class TranslatedMomentDateAdapter extends MomentDateAdapter {
  
  format(date: moment.Moment, displayFormat: string): string {
    // First get the standard formatted date from the parent class
    const formattedDate = super.format(date, displayFormat);
    
    // Then translate the month names
    return this.translateMonthNames(formattedDate);
  }
  
  private translateMonthNames(formattedDate: string): string {
    // Get the user's language from localStorage, default to English
    const language = localStorage.getItem('language') || 'en';
    
    // Replace month names with translated versions
    let translatedDate = formattedDate;
    Object.keys(monthTranslations).forEach(monthName => {
      if (translatedDate.includes(monthName) && monthTranslations[monthName][language]) {
        translatedDate = translatedDate.replace(
          monthName, 
          monthTranslations[monthName][language]
        );
      }
    });
    
    return translatedDate;
  }
}

// Custom date format for MMM d yyyy (e.g., Mar 3 2025)
const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'MM/DD/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'MMMM D, YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'tidy-datepicker',
  templateUrl: 'datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatePickerComponent,
      multi: true,
    },
    { provide: DateAdapter, useClass: TranslatedMomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT }
  ],
})
export class DatePickerComponent extends ValueAccessorBase<any> {
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public icon: string;
  @Input() public form: any;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public noPastDates: boolean;
  @Input() public minDate: string | Date;
  @Input() public minSameDate: boolean = false;
  @Output() optionChange: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild(NgModel, {static: false}) model: NgModel;

  calendarOpened: boolean = false;
  touchedState = false;

  constructor() {
    super();
  }

  changeDate(e) {
    // Convert Moment object to JavaScript Date object if needed
    if (e.value && typeof e.value.toDate === 'function') {
      // This is a Moment object, convert it to a JavaScript Date
      this.value = e.value.toDate();
    } else {
      // Not a Moment object, use as is
      this.value = e.value;
    }
    this.optionChange.emit(this.value);
  }

  changeTouch = () => {
    this.touchedState = true;
  }

  ngOnChanges() {
    if (this.noPastDates) {
      this.minDate = new Date();
      this.minDate.setHours(0, 0 , 0, 0);
    }

    if (this.beforeThanMinDate() && !this.minSameDate) {
      this.value = undefined;
    }
  }

  beforeThanMinDate() {
    const dateValue = new Date(this.value);
    const dateMin = new Date(this.minDate);

    return dateValue < dateMin;
  }

  toggleMatPicker() {
    this.calendarOpened = !this.calendarOpened;
  }

  calendarClosed() {
    this.calendarOpened = false;
  }
}
